import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import {
    MatSnackBar,
    MatSnackBarRef,
    TextOnlySnackBar,
} from '@angular/material/snack-bar';
import { Store, select } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    OffroadTrailType,
    PlannedRideContract,
    PlannedRideRouteSegmentType,
    RideType,
    RouteRequestSegmentContract,
    RoutingResponseContract,
    UserStoreService,
} from '@polaris-customerweb/sharedcomponents.core';
import {
    Adventure,
    Dealer,
    Place,
    PremiumPlace,
    RideArea,
    TrailMapService,
} from '@polaris-customerweb/sharedcomponents.map';
import {
    GeoCoordinate,
    GeoService,
} from '@polaris-customerweb/sharedcomponents.shared';
import {
    PlannedRide,
    PlannedRideRouteSegment,
    PlannedRideTrack,
    PlannedRideViapoint,
    PlannedRideWaypoint,
    ShowOnMapService,
    TrackedRide,
    Waypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { FeatureCollection } from 'geojson';
import { compact, first, flatten, set } from 'lodash-es';
import type { LngLat } from 'mapbox-gl';
import { BehaviorSubject, Observable, combineLatest, forkJoin, of } from 'rxjs';
import {
    catchError,
    filter,
    map,
    switchMap,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import {
    ForcePlannedRideRefresh,
    RemovePlannedRide,
    SetActivePlannedRide,
    SetEditablePlannedRide,
    SetPlannedRides,
    UpdatePlannedRide,
} from 'src/app/components/planned-ride/planned-ride.actions';
import {
    selectActivePlannedRide,
    selectAdhocWaypoint,
    selectIsRidePlanning,
    selectPlannedRides,
    selectShouldForcePlannedRideRefresh,
} from 'src/app/components/planned-ride/planned-ride.reducer';
import { MapboxDirectionsResponseDto } from 'src/app/dtos/mapbox-directions-response.dto';
import { AppState } from 'src/app/models/app-state';
import { GeoJsonFeatureCollection } from 'src/app/models/geojson-feature-collection';
import { selectActiveAdventure } from '../components/adventure/adventure.reducer';
import { selectActiveCollection } from '../components/common/collection/collection.reducer';
import { selectActiveDealer } from '../components/dealer/dealer.reducer';
import { selectActiveGeofence } from '../components/geofence/geofence.reducer';
import {
    selectActivePointOfInterest,
    selectActivePremiumPoi,
} from '../components/point-of-interest/point-of-interest.reducer';
import { selectActiveRideArea } from '../components/ride-area/ride-area.reducer';
import {
    selectActiveTrackedRide,
    selectTrackedRides,
} from '../components/tracked-ride/tracked-ride.reducer';
import {
    selectActivePublicWaypoint,
    selectActiveWaypoint,
} from '../components/waypoint/waypoint.reducer';
import { MapDisplay } from '../models/map-display';
import { MapPoint } from '../models/map-point';
import {
    selectIsShowingDesignatedPavedTrails,
    selectIsShowingDesignatedUnpavedTrails,
    selectIsShowingTrackedRides,
    selectIsShowingUnmaintainedTrails,
    selectSnapToTrackedRideEnabled,
    selectSnapToTrailEnabled,
    selectTrailFilter,
} from '../state/map/layer-visibility/layer-visibility.reducer';
import { UserAuthService } from '../state/user-auth/user-auth.service';
import { LocalStorageService } from './local-storage.service';
import { MapService } from './map.service';
import { PlannedRideResource } from './planned-ride-resource.service';
import { RideTypeService } from './ride-type.service';
import { UuidService } from './uuid.service';

const UNSAVED_PLANNED_RIDE_KEY = 'unsavedPlannedRide';
const LAST_PLANNED_RIDE_URL_KEY = 'lastPlannedRideUrl';

@Injectable({ providedIn: 'root' })
export class PlannedRideService {
    routableItem$: Observable<MapPoint>;
    nonRoutableItem$: Observable<MapPoint>;
    unroutableError$ = new BehaviorSubject<boolean>(false);
    private plannedRides: PlannedRide[] = [];
    private rideType: RideType;

    private snapToTrailEnabled: boolean;
    private snapToTrackedEnabled: boolean;
    private activeTrailTypes: OffroadTrailType[];
    private trackedRides: TrackedRide[];
    private showingTrackedRides: boolean;
    private activeSnackBar: MatSnackBarRef<TextOnlySnackBar>;
    private defaultRouteName = $localize`Route `;

    constructor(
        private configService: ConfigService,
        private localStore: LocalStorageService,
        private mapService: MapService,
        private matSnackBar: MatSnackBar,
        private plannedRideResource: PlannedRideResource,
        private rideTypeService: RideTypeService,
        private showOnMapService: ShowOnMapService,
        private store: Store<AppState>,
        private trailMapService: TrailMapService,
        private userAuthService: UserAuthService,
        private uuidService: UuidService,
        private userStoreService: UserStoreService,
        @Inject(LOCALE_ID) public locale: string
    ) {
        store
            .pipe(select(selectPlannedRides))
            .subscribe((plannedRides) => (this.plannedRides = plannedRides));

        this.rideTypeService.rideType$
            .pipe(filter((rideType) => Boolean(rideType)))
            .subscribe((rideType) => (this.rideType = rideType || 'ORV'));

        this.store
            .select(selectSnapToTrailEnabled)
            .subscribe((snapToTrailEnabled) => {
                this.snapToTrailEnabled = snapToTrailEnabled;
            });

        this.store
            .select(selectSnapToTrackedRideEnabled)
            .subscribe((snapEnabled) => {
                this.snapToTrackedEnabled = snapEnabled;
            });

        combineLatest([
            this.rideTypeService.rideType$,
            this.store.select(selectTrailFilter),
            this.store.select(selectIsShowingDesignatedPavedTrails),
            this.store.select(selectIsShowingDesignatedUnpavedTrails),
            this.store.select(selectIsShowingUnmaintainedTrails),
        ]).subscribe(([type, trailType, dp, dup, unm]) => {
            this.activeTrailTypes = [];
            if (type === 'ORV') {
                if (!trailType) {
                    this.activeTrailTypes = [
                        'OHM',
                        'ATV',
                        'ROV50',
                        'ROV',
                        'HLV',
                    ];
                } else {
                    this.activeTrailTypes = [trailType];
                }
            } else {
                this.activeTrailTypes.push('Snow');
            }
            if (dp) {
                this.activeTrailTypes.push('UndesignatedPaved');
            }
            if (dup) {
                this.activeTrailTypes.push('UndesignatedUnpaved');
            }
            if (unm) {
                this.activeTrailTypes.push('UndesignatedUnmaintained');
            }
        });

        this.store
            .select(selectTrackedRides)
            .subscribe((trackedRides) => (this.trackedRides = trackedRides));

        this.store
            .select(selectIsShowingTrackedRides)
            .subscribe(
                (showingTracked) => (this.showingTrackedRides = showingTracked)
            );
        this.routableItem$ = combineLatest([
            store.select(selectActiveDealer),
            store.select(selectActivePointOfInterest),
            store.select(selectActivePremiumPoi),
            store.select(selectActivePublicWaypoint),
            store.select(selectActiveRideArea),
            store.select(selectActiveWaypoint),
            store.select(selectAdhocWaypoint),
            store.select(selectActiveAdventure),
        ]).pipe(map((selectedItems) => compact(selectedItems)[0]));
        this.nonRoutableItem$ = combineLatest([
            this.store.select(selectActiveGeofence),
            this.store.select(selectActiveCollection),
            this.store.select(selectActiveTrackedRide),
            this.store.select(selectActivePlannedRide),
        ]).pipe(map((selectedItems) => compact(selectedItems)[0]));
    }
    get MAX_POINTS(): number {
        return this.configService.isOnRoad ? 25 : Infinity;
    }

    calculateRouteBetweenWaypoints(
        mapboxWaypoints: string[],
        excludeRouteOptions?: string
    ): Observable<MapboxDirectionsResponseDto> {
        return this.plannedRideResource
            .getRoute(mapboxWaypoints, excludeRouteOptions)
            .pipe(
                catchError((response: HttpErrorResponse) => {
                    const error = response?.error as { message?: string };
                    const errorMessage = error?.message || '';
                    if (
                        response.status === 422 &&
                        errorMessage?.includes('maximum distance')
                    ) {
                        return of({
                            code: 'NoRoute',
                            routes: [],
                            waypoints: [],
                        });
                    }
                    this.showErrorMessageRouting('Failure');
                    return of({ code: 'Failure', routes: [], waypoints: [] });
                })
            );
    }

    //#region Get
    getPlannedRides$(): Observable<PlannedRide[]> {
        return combineLatest([
            this.store.select(selectShouldForcePlannedRideRefresh),
            this.userAuthService.isAuthenticated$,
        ]).pipe(
            switchMap(([shouldForce, isAuthenticated]) => {
                if (!isAuthenticated) {
                    return of([] as PlannedRide[]);
                }
                if (shouldForce) {
                    return this.plannedRideResource.getPlannedRides().pipe(
                        switchMap((contracts) => {
                            const plannedRides = contracts.map(
                                (dto) =>
                                    new PlannedRide(
                                        dto,
                                        this.configService.appSku
                                    )
                            );

                            this.store.dispatch(
                                new SetPlannedRides(plannedRides)
                            );
                            this.store.dispatch(
                                new ForcePlannedRideRefresh(false)
                            );
                            return this.store.pipe(select(selectPlannedRides));
                        })
                    );
                } else {
                    return this.store.pipe(select(selectPlannedRides));
                }
            })
        );
    }

    getPlannedRideById(
        id: string,
        forceRefresh = false
    ): Observable<PlannedRide> {
        let plannedRide: PlannedRide | undefined;
        if (!forceRefresh) {
            plannedRide = this.getPlannedRideByIdFromCache(id);
        }

        if (plannedRide) {
            return of(plannedRide);
        } else if (this.uuidService.isEmpty(id)) {
            return of(new PlannedRide(undefined, this.configService.appSku));
        } else {
            return this.plannedRideResource.getPlannedRideById(id).pipe(
                map(
                    (plannedRideDto) =>
                        new PlannedRide(
                            plannedRideDto,
                            this.configService.appSku
                        )
                ),
                tap((newPlannedRide) => {
                    this.store.dispatch(new UpdatePlannedRide(newPlannedRide));
                })
            );
        }
    }

    getPlannedRideByIdFromCache(id: string): PlannedRide | undefined {
        return first(this.plannedRides.filter((ride) => ride.id === id));
    }

    getPlannedRidesWithMatchingWaypoint$(
        id: string
    ): Observable<PlannedRide[]> {
        if (!this.isValidId(id)) {
            return of<PlannedRide[]>([]);
        }

        return this.store.pipe(
            select(selectPlannedRides),
            map((plannedRides) =>
                plannedRides.filter((item) =>
                    item.waypoints.some(
                        (waypoint) => waypoint.originalId === id
                    )
                )
            )
        );
    }

    //#endregion

    //#region Create
    createPlannedRide(plannedRide: PlannedRide): Observable<PlannedRide> {
        this.validateRideType(plannedRide);
        this.formatDisplayName(plannedRide);

        const plannedRideDto = plannedRide.toContract();

        if (!plannedRideDto.featureCollection?.features?.[0]?.properties) {
            set(
                plannedRideDto,
                ['featureCollection', 'features', 0, 'properties'],
                {}
            );
        }
        set(
            plannedRideDto,
            [
                'featureCollection',
                'features',
                0,
                'properties',
                'routingTrailTypes',
            ],
            this.activeTrailTypes
        );
        if (
            plannedRide.features.some(
                (feature) => feature instanceof PlannedRideRouteSegment
            )
        ) {
            set(
                plannedRideDto,
                ['featureCollection', 'features', 0, 'geometry'],
                null
            );
        }
        plannedRideDto.id = this.uuidService.generate();

        return this.plannedRideResource.updatePlannedRide(plannedRideDto).pipe(
            switchMap(() =>
                this.getPlannedRideById(plannedRideDto.id || '', true)
            ),
            tap((newPlannedRide) => {
                this.showOnMap(newPlannedRide);
                this.store.dispatch(new UpdatePlannedRide(newPlannedRide));
            })
        );
    }

    //#endregion

    //#region Update
    updatePlannedRide(plannedRide: PlannedRide): Observable<PlannedRide> {
        this.validateRideType(plannedRide);
        const plannedRideDto = plannedRide.toContract();
        const currentDate = new Date().toISOString();

        set(
            plannedRideDto,
            ['featureCollection', 'features', 0, 'properties'],
            plannedRideDto?.featureCollection?.features?.[0]?.properties ?? {}
        );
        set(
            plannedRideDto,
            [
                'featureCollection',
                'features',
                0,
                'properties',
                'routingTrailTypes',
            ],
            this.activeTrailTypes
        );
        if (
            !plannedRideDto.modificationDate ||
            currentDate > plannedRideDto.modificationDate
        ) {
            plannedRideDto.modificationDate = currentDate;
        }

        return this.plannedRideResource.updatePlannedRide(plannedRideDto).pipe(
            switchMap(() => this.getPlannedRideById(plannedRide.id, true)),
            tap((newPlannedRide) => {
                this.store.dispatch(new UpdatePlannedRide(newPlannedRide));
            })
        );
    }

    updateFlyByUrl(id: string): Observable<string> {
        return this.plannedRideResource.updateFlyByUrl(id);
    }

    //#endregion

    //#region Delete
    deletePlannedRide(id: string): Observable<void> {
        return this.plannedRideResource.deletePlannedRide(id).pipe(
            tap(() => {
                this.store.dispatch(new RemovePlannedRide(id));
            })
        );
    }

    deleteSelectedPlannedRides(
        plannedRides: PlannedRide[]
    ): Observable<{ id: string; success: boolean }[]> {
        if (plannedRides.length === 0) {
            return of([]);
        }

        const resultObservables = plannedRides.map(
            (plannedRide: PlannedRide) => {
                return this.deletePlannedRide(plannedRide.id).pipe(
                    catchError(() => {
                        return of('failed');
                    }),
                    map((result) => {
                        return {
                            id: plannedRide.id,
                            success: result !== 'failed',
                        };
                    })
                );
            }
        );

        return forkJoin(resultObservables);
    }

    //#endregion

    //#region Unsaved planned ride
    getUnsavedPlannedRide(): PlannedRide | null {
        let unsavedPlannedRide: PlannedRide;
        const unsavedRideString = this.userStoreService.get(
            UNSAVED_PLANNED_RIDE_KEY
        );
        if (unsavedRideString) {
            unsavedPlannedRide = new PlannedRide(
                JSON.parse(unsavedRideString) as PlannedRideContract,
                this.configService.appSku
            );

            this.moveUnsavedLoggedOutRideToUserLocalStorage(unsavedPlannedRide);

            if (unsavedPlannedRide.points.length > 0) {
                return unsavedPlannedRide;
            }
        }

        return null;
    }

    savePlannedRideLocally(plannedRide: PlannedRide): void {
        const dataString = JSON.stringify(plannedRide.toContract());
        this.userStoreService.set(UNSAVED_PLANNED_RIDE_KEY, dataString);
    }

    clearUnsavedPlannedRide(): void {
        this.userStoreService.remove(UNSAVED_PLANNED_RIDE_KEY);
    }

    hasUnsavedPlannedRide(): boolean {
        return Boolean(this.getUnsavedPlannedRide());
    }

    //#endregion

    //#region last planned ride url

    getLastPlannedRideUrl(): string {
        return this.userStoreService.get(LAST_PLANNED_RIDE_URL_KEY) || '';
    }

    setLastPlannedRideUrl(url): void {
        this.userStoreService.set(LAST_PLANNED_RIDE_URL_KEY, url);
    }

    clearLastPlannedRideUrl(): void {
        this.userStoreService.remove(LAST_PLANNED_RIDE_URL_KEY);
    }

    //#endregion

    convertGpxToPlannedRide(formData: FormData): Observable<PlannedRide> {
        return this.plannedRideResource
            .convertGpxToPlannedRide(formData)
            .pipe(
                map(
                    (plannedRideDto) =>
                        new PlannedRide(
                            plannedRideDto,
                            this.configService.appSku
                        )
                )
            );
    }

    plannedRidesToGeoJson(
        plannedRides: PlannedRide[] = []
    ): GeoJsonFeatureCollection {
        const features = flatten([
            ...plannedRides.map(
                (plannedRide) => plannedRide.toGeoJson().features
            ),
        ]);
        return new GeoJsonFeatureCollection(features);
    }

    setPlannedRideTrackFromMapboxDirections(
        plannedRide: PlannedRide,
        excludeRouteOptions?: string
    ): Observable<PlannedRide> {
        this.unroutableError$.next(false);
        const waypointLocations = plannedRide.points.map((waypoint) =>
            waypoint.toMapboxCoordinateString()
        );
        const newPlannedRide = plannedRide.clone();

        if (waypointLocations.length > 1) {
            return this.calculateRouteBetweenWaypoints(
                waypointLocations,
                excludeRouteOptions
            ).pipe(
                map((response) => {
                    if (
                        response.code === 'NoRoute' ||
                        response.code === 'NoSegment'
                    ) {
                        this.showErrorMessageRouting(response.code);
                        this.unroutableError$.next(true);
                        return newPlannedRide;
                    }
                    const route = response.routes[0];
                    const coords = route.geometry.coordinates;
                    newPlannedRide.ride = PlannedRideTrack.fromCoordinateArray(
                        coords,
                        route.duration,
                        route.distance,
                        newPlannedRide.ride.rideColor
                    );
                    // the route has legs that defines the distance and duration in between them.
                    const legs = route.legs;
                    let distance = 0;
                    let duration = 0;
                    let point: PlannedRideWaypoint | PlannedRideViapoint;

                    for (let i = 0; i < newPlannedRide.points.length; i++) {
                        point = newPlannedRide.points[i];
                        if (!point) {
                            break;
                        }
                        if (!legs[i]) {
                            break;
                        }

                        distance += legs[i].distance;
                        duration += legs[i].duration;

                        // We need to get all the legs between waypoints - there can be multiples due to viapoints
                        // Then we add the leg distances together to get the entire distance between waypoints.
                        while (i < newPlannedRide.points.length) {
                            const nextIndex = i + 1;
                            const nextPoint = newPlannedRide.points[nextIndex];
                            if (
                                !nextPoint ||
                                nextPoint instanceof PlannedRideWaypoint
                            ) {
                                // The next point is a waypoint, so there's no need to do any further work
                                break;
                            } else {
                                // We found a viapoint, so we need to add that leg too
                                const leg = legs[nextIndex];
                                if (!leg) {
                                    break;
                                }
                                distance += leg.distance;
                                duration += leg.duration;
                                i = nextIndex;
                            }
                        }

                        if (point instanceof PlannedRideWaypoint) {
                            point.distance = distance;
                            point.duration = duration;
                            distance = 0;
                            duration = 0;
                        }
                    }

                    // snap points to roads
                    newPlannedRide.points.forEach((p) => {
                        const routeCoordinates =
                            newPlannedRide.ride.geometry?.coordinates || [];
                        const location = GeoService.findClosestPoint(
                            routeCoordinates,
                            p.geometry.coordinates
                        );
                        p.geometry = {
                            coordinates: location,
                            type: 'Point',
                        };
                    });

                    return newPlannedRide;
                })
            );
        } else if (waypointLocations.length === 1) {
            const coordinates = plannedRide.points.map(
                (waypoint) => waypoint.geometry.coordinates
            );
            newPlannedRide.ride = PlannedRideTrack.fromCoordinateArray(
                coordinates,
                undefined,
                undefined,
                newPlannedRide.ride.rideColor
            );

            return of(newPlannedRide);
        } else {
            newPlannedRide.ride = PlannedRideTrack.fromCoordinateArray(
                [],
                undefined,
                undefined,
                newPlannedRide.ride.rideColor
            );
            return of(newPlannedRide);
        }
    }

    removeFromMap(ride: PlannedRide) {
        this.showOnMapService.setShouldShow(ride.id, false);
        this.store.dispatch(new UpdatePlannedRide(ride));
    }

    showOnMap(ride: PlannedRide) {
        this.showOnMapService.setShouldShow(ride.id, true);
        this.store.dispatch(new UpdatePlannedRide(ride));
    }

    addWaypointFromLatLng(
        newPlannedRide: PlannedRide,
        lngLat: LngLat
    ): PlannedRide {
        const waypoint = PlannedRideWaypoint.fromMapboxLngLat(
            lngLat,
            undefined,
            this.locale
        );
        waypoint.originalType = 'generated';

        newPlannedRide.addPoint(waypoint);

        if (newPlannedRide.points.length > 2) {
            newPlannedRide.downgradePreviousEndWaypoint();
        }
        return newPlannedRide;
    }

    // Offroad click/drag trail to create
    // a new control point in the middle of the track
    insertPointWithSegments(
        mapDisplay: MapDisplay,
        plannedRide: PlannedRide,
        point: PlannedRideViapoint,
        index: number
    ) {
        const prior = plannedRide.points[index - 1];
        // add the new point right after the previous point,
        // updateSegmentsAroundPoint will handle removing old segments and
        // adding new temporary segments
        plannedRide.features = [
            ...plannedRide.features.slice(
                0,
                plannedRide.features.indexOf(prior) + 1
            ),
            point,
            ...plannedRide.features.slice(
                plannedRide.features.indexOf(prior) + 1
            ),
        ];

        this.updateSegmentsAroundPoint(mapDisplay, plannedRide, point);
    }

    // Offroad Add a Point
    addWaypointFromLatLngWithSegments(
        mapDisplay: MapDisplay,
        plannedRide: PlannedRide,
        lngLat: LngLat
    ) {
        const updatedPlannedRide = this.addWaypointFromLatLng(
            plannedRide,
            lngLat
        );
        this.trackLastWaypoint(mapDisplay, updatedPlannedRide);
    }

    trackLastWaypoint(mapDisplay: MapDisplay, plannedRide: PlannedRide) {
        let start: PlannedRideWaypoint | PlannedRideViapoint;
        let end: PlannedRideWaypoint | PlannedRideViapoint;
        if (plannedRide.hasMinimumPoints) {
            start = plannedRide.points[plannedRide.points.length - 2];
            end = plannedRide.points[plannedRide.points.length - 1];
            const r1 = this.addTempSegment(plannedRide.features, start, end);
            plannedRide.features = r1.newFeatures;
            this.routeTempSegments(mapDisplay, [r1.tempSegment]);
        }

        this.store.dispatch(new SetActivePlannedRide(plannedRide));
        this.store.dispatch(
            new SetEditablePlannedRide(
                plannedRide,
                !plannedRide.isWaitingOnRouting
            )
        );
    }

    // Offroad delete a point
    deletePoint(
        mapDisplay: MapDisplay,
        plannedRide: PlannedRide,
        point: PlannedRideWaypoint | PlannedRideViapoint,
        deleteViapoints = false,
        isOffroad = true
    ) {
        let prior: PlannedRideWaypoint | PlannedRideViapoint | null = null;
        let subsequent: PlannedRideWaypoint | PlannedRideViapoint | null = null;
        if (deleteViapoints && point instanceof PlannedRideWaypoint) {
            const index = plannedRide.waypoints.indexOf(point);
            prior = index !== 0 ? plannedRide.waypoints[index - 1] : null;
            subsequent = plannedRide.waypoints[index + 1];
        } else if (deleteViapoints && point instanceof PlannedRideViapoint) {
            const index = plannedRide.points.indexOf(point);
            for (let i = 0; i < plannedRide.points.length; i++) {
                const currPoint = plannedRide.points[i];
                if (i < index && currPoint instanceof PlannedRideWaypoint) {
                    prior = currPoint;
                } else if (
                    i > index &&
                    currPoint instanceof PlannedRideWaypoint
                ) {
                    subsequent = currPoint;
                    break;
                }
            }
        } else {
            const index = plannedRide.points.indexOf(point);
            prior = index !== 0 ? plannedRide.points[index - 1] : null;
            subsequent = plannedRide.points[index + 1];
        }
        const featuresToRemove = this.getFeaturesToRemove(
            plannedRide.features,
            prior,
            subsequent
        );
        const newFeatures = plannedRide.features.filter(
            (feature) => !featuresToRemove.includes(feature)
        );
        if (prior && subsequent && isOffroad) {
            const r1 = this.addTempSegment(newFeatures, prior, subsequent);
            plannedRide.features = r1.newFeatures;
            this.routeTempSegments(mapDisplay, [r1.tempSegment]);
        } else {
            plannedRide.features = [...newFeatures];
            // if we deleted the first or last waypoint but we aren't deleting
            // surrounding viapoints we need to upgrade the new first or last
            // viapoint to a waypoint
            if (point instanceof PlannedRideWaypoint && !deleteViapoints) {
                if (prior && prior instanceof PlannedRideViapoint) {
                    plannedRide.upgradeViapoint(prior, false, this.locale);
                } else if (
                    subsequent &&
                    subsequent instanceof PlannedRideViapoint
                ) {
                    plannedRide.upgradeViapoint(subsequent, false, this.locale);
                }
            }
        }
        if (isOffroad) {
            this.store.dispatch(new SetActivePlannedRide(plannedRide));
            this.store.dispatch(
                new SetEditablePlannedRide(
                    plannedRide,
                    !plannedRide.isWaitingOnRouting
                )
            );
        }
    }

    // Offroad click and drag a point
    updateSegmentsAroundPoint(
        mapDisplay: MapDisplay,
        plannedRide: PlannedRide,
        point: PlannedRideViapoint | PlannedRideWaypoint,
        route = false
    ) {
        const index = plannedRide.points.indexOf(point);
        const prior = index !== 0 ? plannedRide.points[index - 1] : null;
        const subsequent = plannedRide.points[index + 1];

        let featuresToRemove = this.getFeaturesToRemove(
            plannedRide.features,
            prior,
            point
        );

        featuresToRemove = featuresToRemove.concat(
            this.getFeaturesToRemove(plannedRide.features, point, subsequent)
        );

        let newFeatures = plannedRide.features.filter(
            (feature) => !featuresToRemove.includes(feature)
        );
        const segments: PlannedRideRouteSegment[] = [];
        if (prior) {
            const r1 = this.addTempSegment(newFeatures, prior, point);
            newFeatures = r1.newFeatures;
            if (route) {
                segments.push(r1.tempSegment);
            }
        }
        if (subsequent) {
            const r2 = this.addTempSegment(newFeatures, point, subsequent);
            newFeatures = r2.newFeatures;
            if (route) {
                segments.push(r2.tempSegment);
            }
        }

        if (segments.length) {
            this.routeTempSegments(mapDisplay, segments);
        }

        plannedRide.features = newFeatures;
    }

    updateSegmentsAfterMoving(
        mapDisplay: MapDisplay,
        plannedRide: PlannedRide,
        point: PlannedRideWaypoint | PlannedRideViapoint
    ) {
        // a clone of the point was added to features,
        // so we'll find by id in this case
        const actualPoint = plannedRide.features.find(
            (x) =>
                (x instanceof PlannedRideViapoint ||
                    x instanceof PlannedRideWaypoint) &&
                x.id === point?.id
        ) as PlannedRideWaypoint | PlannedRideViapoint;
        this.updateSegmentsAroundPoint(
            mapDisplay,
            plannedRide,
            actualPoint,
            true
        );
        this.store.dispatch(new SetActivePlannedRide(plannedRide));
        this.store.dispatch(
            new SetEditablePlannedRide(
                plannedRide,
                !plannedRide.isWaitingOnRouting
            )
        );
    }

    // Offroad Rearrange Two Points
    rearrangeWaypoints(
        mapDisplay: MapDisplay,
        plannedRide: PlannedRide,
        oldIndex: number,
        newIndex: number
    ) {
        // get point references
        // need to adjust offset based on whether
        // new position is before or after the old position
        const offset = newIndex > oldIndex ? 0 : 1;
        const point = plannedRide.waypoints[oldIndex];
        const oldPrior =
            oldIndex !== 0 ? plannedRide.waypoints[oldIndex - 1] : null;
        const oldSubsequent = plannedRide.waypoints[oldIndex + 1];
        const newPrior =
            newIndex !== 0 ? plannedRide.waypoints[newIndex - offset] : null;
        const newSubsequent = plannedRide.waypoints[newIndex + 1 - offset];

        // old location surrounding segments
        let featsToRemove = this.getFeaturesToRemove(
            plannedRide.features,
            oldPrior,
            oldSubsequent
        );
        // old point
        featsToRemove.push(plannedRide.waypoints[oldIndex]);
        // new location surrounding segments
        featsToRemove = featsToRemove.concat(
            this.getFeaturesToRemove(
                plannedRide.features,
                newPrior,
                newSubsequent
            )
        );

        let newFeatures = plannedRide.features.filter(
            (feature) => !featsToRemove.includes(feature)
        );

        // add point in new spot
        if (newPrior) {
            newFeatures = [
                ...newFeatures.slice(0, newFeatures.indexOf(newPrior) + 1),
                point,
                ...newFeatures.slice(newFeatures.indexOf(newPrior) + 1),
            ];
        } else {
            newFeatures = [newFeatures[0], point, ...newFeatures.slice(1)];
        }

        const segments: PlannedRideRouteSegment[] = [];

        // recalc segment where old point used to be
        if (oldPrior && oldSubsequent) {
            const r1 = this.addTempSegment(
                newFeatures,
                oldPrior,
                oldSubsequent
            );
            newFeatures = r1.newFeatures;
            segments.push(r1.tempSegment);
        }
        // recalc segment between new previous point and new position
        if (newPrior) {
            const r2 = this.addTempSegment(newFeatures, newPrior, point);
            newFeatures = r2.newFeatures;
            segments.push(r2.tempSegment);
        }
        // recalc segment between new and new subsequent point
        if (newSubsequent) {
            const r3 = this.addTempSegment(newFeatures, point, newSubsequent);
            newFeatures = r3.newFeatures;
            segments.push(r3.tempSegment);
        }

        if (segments) {
            this.routeTempSegments(mapDisplay, segments);
        }
        plannedRide.features = newFeatures;
        this.store.dispatch(new SetActivePlannedRide(plannedRide));
        this.store.dispatch(
            new SetEditablePlannedRide(
                plannedRide,
                !plannedRide.isWaitingOnRouting
            )
        );
    }

    // Offroad Reverse Points
    reverseWaypoints(mapDisplay: MapDisplay, plannedRide: PlannedRide) {
        const segments: PlannedRideRouteSegment[] = [];
        const waypoints = plannedRide.features.filter(
            (feature) =>
                feature instanceof PlannedRideWaypoint ||
                feature instanceof PlannedRideViapoint
        );
        const newFeatures: (
            | PlannedRideTrack
            | PlannedRideWaypoint
            | PlannedRideViapoint
            | PlannedRideRouteSegment
        )[] = plannedRide.features.filter(
            (feature) => feature instanceof PlannedRideTrack
        );

        for (let index = waypoints.length - 1; index >= 0; index--) {
            newFeatures.push(waypoints[index] as unknown as PlannedRideTrack);

            if (index > 0) {
                const tempSegment =
                    PlannedRideRouteSegment.offroadFromGeoCoordinates(
                        waypoints[index].geoCoordinate,
                        waypoints[index - 1].geoCoordinate
                    );
                newFeatures.push(tempSegment as unknown as PlannedRideTrack);
                segments.push(tempSegment);
            }
        }

        if (segments) {
            this.routeTempSegments(mapDisplay, segments);
        }
        plannedRide.features = newFeatures;
        this.store.dispatch(new SetActivePlannedRide(plannedRide));
        this.store.dispatch(
            new SetEditablePlannedRide(
                plannedRide,
                !plannedRide.isWaitingOnRouting
            )
        );
    }

    dismissActiveSnackBar() {
        this.activeSnackBar?.dismiss();
    }

    generateGenericOrdinalRouteName(): string {
        let routeNumbers: number[] = [];
        const pattern = `${this.defaultRouteName}(\\d)+$`;
        const regex = new RegExp(pattern, 'i');

        this.plannedRides.forEach((plannedRide) => {
            const { name } = plannedRide;
            if (regex.test(name)) {
                const nameParts = name.split(' ');
                const lastPart = nameParts[nameParts.length - 1];
                const routeNumber = parseInt(lastPart, 10);

                if (!isNaN(routeNumber)) {
                    routeNumbers = [...routeNumbers, routeNumber];
                }
            }
        });

        const smallestUnusedRouteNumber =
            this.getSmallestUnusedRouteNumber(routeNumbers);
        const nextRoute = `${this.defaultRouteName}${smallestUnusedRouteNumber}`;

        return nextRoute;
    }
    getRoutableItemAnalyticsEntityType(mapPoint: MapPoint) {
        return mapPoint instanceof Dealer
            ? 'Dealer'
            : mapPoint instanceof PremiumPlace
              ? 'PremiumPOI'
              : mapPoint instanceof Place
                ? 'POI'
                : mapPoint instanceof Waypoint
                  ? mapPoint.isPublic
                      ? 'PublicWaypoint'
                      : 'Waypoint'
                  : mapPoint instanceof PlannedRideWaypoint
                    ? 'AdhocWaypoint'
                    : mapPoint instanceof PlannedRideViapoint
                      ? 'AdhocViapoint'
                      : mapPoint instanceof Adventure
                        ? 'Adventure'
                        : mapPoint instanceof RideArea
                          ? 'RideArea'
                          : undefined;
    }

    private validateRideType(plannedRide: PlannedRide) {
        if (this.configService.isOffRoad && !plannedRide.rideType) {
            plannedRide.rideType = this.rideType;
        }
        if (!plannedRide.appSku) {
            plannedRide.appSku = this.configService.appSku;
        }
    }

    private formatDisplayName(plannedRide: PlannedRide) {
        plannedRide.features
            .filter((feature) => feature instanceof PlannedRideWaypoint)
            .map((waypoint: PlannedRideWaypoint) => {
                if (waypoint.isNameLatLngString) {
                    waypoint.name = waypoint.geoCoordinate.toShortLocaleString(
                        4,
                        this.locale
                    );
                }
            });
    }

    private getFeaturesToRemove(
        features: readonly (
            | PlannedRideWaypoint
            | PlannedRideViapoint
            | PlannedRideTrack
            | PlannedRideRouteSegment
        )[],
        startPoint: PlannedRideWaypoint | PlannedRideViapoint | null,
        endPoint: PlannedRideWaypoint | PlannedRideViapoint | null
    ) {
        let remove: (
            | PlannedRideWaypoint
            | PlannedRideViapoint
            | PlannedRideTrack
            | PlannedRideRouteSegment
        )[] = [];
        for (const feat of features) {
            if (startPoint === feat) {
                remove = [];
            } else if (endPoint === feat) {
                break;
            } else if (!(feat instanceof PlannedRideTrack)) {
                remove.push(feat);
            }
        }
        return remove;
    }

    private addTempSegment(
        features: readonly (
            | PlannedRideWaypoint
            | PlannedRideTrack
            | PlannedRideRouteSegment
            | PlannedRideViapoint
        )[],
        priorPoint: PlannedRideWaypoint | PlannedRideViapoint,
        subsequentPoint: PlannedRideWaypoint | PlannedRideViapoint
    ) {
        const tempSegment = PlannedRideRouteSegment.offroadFromGeoCoordinates(
            priorPoint?.geoCoordinate,
            subsequentPoint?.geoCoordinate
        );
        const index = features.indexOf(priorPoint);
        return {
            newFeatures: [
                ...features.slice(0, index + 1),
                tempSegment,
                ...features.slice(index + 1),
            ],
            tempSegment,
        };
    }

    private routeTempSegments(
        mapDisplay: MapDisplay,
        segments: PlannedRideRouteSegment[]
    ) {
        const trackedRidesInView = this.getUserTracks(mapDisplay, segments);
        if (
            (this.snapToTrailEnabled && this.mapDisplayingTrails(mapDisplay)) ||
            (this.snapToTrackedEnabled && trackedRidesInView.length > 0)
        ) {
            segments.forEach((segment) => (segment.waitSpinner = true));
            this.getRoutingForSegments(segments, trackedRidesInView)
                .pipe(
                    withLatestFrom(
                        this.store.select(selectActivePlannedRide),
                        this.store.select(selectIsRidePlanning)
                    ),
                    filter(([, , isRidePlanning]) => isRidePlanning)
                )
                .subscribe(([segmentsList, plannedRide]) => {
                    if (!plannedRide) {
                        return;
                    }
                    for (let i = 0; i < segments.length; i++) {
                        const index = plannedRide.features.indexOf(segments[i]);
                        if (index > -1) {
                            plannedRide.features = [
                                ...plannedRide.features.slice(0, index),
                                ...segmentsList[i],
                                ...plannedRide.features.slice(index + 1),
                            ];
                        }
                    }

                    this.store.dispatch(new SetActivePlannedRide(plannedRide));
                    this.store.dispatch(
                        new SetEditablePlannedRide(
                            plannedRide,
                            !plannedRide?.isWaitingOnRouting
                        )
                    );
                });
        }
    }

    private getRoutingForSegments(
        segments: PlannedRideRouteSegment[],
        trackedRides: TrackedRide[]
    ): Observable<PlannedRideRouteSegment[][]> {
        return this.plannedRideResource
            .getTrailRoute(
                segments.map(
                    (seg) =>
                        ({
                            endLat: seg.geoCoordinates[1].lat,
                            endLon: seg.geoCoordinates[1].lng,
                            startLat: seg.geoCoordinates[0].lat,
                            startLon: seg.geoCoordinates[0].lng,
                        }) as RouteRequestSegmentContract
                ),
                this.snapToTrailEnabled ? this.activeTrailTypes : [],
                trackedRides
            )
            .pipe(
                map((res: RoutingResponseContract) => {
                    if (res?.results) {
                        // TODO: verify contract for below override
                        return (
                            res.results as unknown as FeatureCollection[]
                        ).map((result) => {
                            return result.features.map((segment) => {
                                const geoJson =
                                    segment.geometry as GeoJSON.LineString;
                                //don't show trail jump start/end too long for partial success
                                if (
                                    segment.properties &&
                                    segment.properties.errorTitle &&
                                    segment.properties.errorTitle !==
                                        'StartJumpTooLong' &&
                                    segment.properties.errorTitle !==
                                        'EndJumpTooLong'
                                ) {
                                    this.showErrorMessageRouting(
                                        segment.properties.errorTitle as string
                                    );
                                }
                                return PlannedRideRouteSegment.fromCoordinateArray(
                                    geoJson.coordinates,
                                    segment.properties?.distance !== undefined
                                        ? (segment.properties
                                              .distance as number)
                                        : GeoCoordinate.pointDistanceInMeters(
                                              GeoCoordinate.fromCoordinateArray(
                                                  geoJson.coordinates[0]
                                              ),
                                              GeoCoordinate.fromCoordinateArray(
                                                  geoJson.coordinates[1]
                                              )
                                          ),
                                    segment.properties
                                        ?.segmentType as PlannedRideRouteSegmentType,
                                    this.snapToTrailEnabled,
                                    this.snapToTrackedEnabled
                                );
                            });
                        });
                    } else {
                        return segments.map((segment) => [
                            PlannedRideRouteSegment.offroadFromGeoCoordinates(
                                segment.geoCoordinates[0],
                                segment.geoCoordinates[1],
                                this.snapToTrailEnabled,
                                this.snapToTrackedEnabled
                            ),
                        ]);
                    }
                }),
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 417) {
                        this.showErrorMessageRouting(
                            (error?.error?.results?.[0]?.features?.[0]
                                ?.properties?.errorTitle as string) || 'Failure'
                        );
                    } else {
                        this.showErrorMessageRouting('Failure');
                    }

                    // If error occurred, Set snap to false  to show the tempViapoint in the middle of the offTrail segment (thru getCenterBetweenPoints).
                    return of(
                        segments.map((segment) => [
                            PlannedRideRouteSegment.offroadFromGeoCoordinates(
                                segment.geoCoordinates[0],
                                segment.geoCoordinates[1],
                                false,
                                false
                            ),
                        ])
                    );
                })
            );
    }

    //
    private showErrorMessageRouting(errorTitle: string) {
        if (
            errorTitle === 'StartJumpTooLong' ||
            errorTitle === 'EndJumpTooLong'
        ) {
            this.displayActiveSnackbar(
                $localize`Add points closer to a trail or track to snap.`
            );
        }

        if (errorTitle === 'TrailJumpTooLong') {
            this.displayActiveSnackbar(
                $localize`Unable to find a route between trails/tracks.`
            );
        }

        if (errorTitle === 'TimeOut' || errorTitle === 'SegmentTooLong') {
            this.displayActiveSnackbar(
                $localize`Try a shorter distance for snapping.`
            );
        }

        if (errorTitle === 'Failure') {
            this.displayActiveSnackbar(
                $localize`Doh! The hamsters are tired. Try snapping later, or continue without snapping.`,
                true
            );
        }

        if (errorTitle === 'NoRoute' || errorTitle === 'NoSegment') {
            this.displayActiveSnackbar($localize`Maximum ride length reached.`);
        }
    }

    private displayActiveSnackbar(message: string, isFailure = false) {
        this.activeSnackBar = this.matSnackBar.open(message, '', {
            duration: 10000,
            panelClass: [
                'mat-snackbar',
                isFailure
                    ? 'warning-failure-mat-snackbar'
                    : 'warning-regular-mat-snackbar',
            ],
            verticalPosition: 'top',
        });
    }

    private mapDisplayingTrails(mapDisplay: MapDisplay): boolean {
        return (
            this.activeTrailTypes.length > 0 &&
            this.trailMapService.mapIsDisplayingAnyTrailFeatures(
                mapDisplay.map,
                this.activeTrailTypes,
                this.rideType
            )
        );
    }

    private getUserTracks(
        mapDisplay: MapDisplay,
        segments: PlannedRideRouteSegment[]
    ): TrackedRide[] {
        if (
            this.snapToTrackedEnabled &&
            this.showingTrackedRides &&
            this.trackedRides
        ) {
            let mapBounds = this.mapService.getBounds(mapDisplay.map);
            for (const segment of segments) {
                mapBounds = mapBounds.expandToIncludeGeoCoordinate(
                    segment.geoCoordinates[0]
                );
                mapBounds = mapBounds.expandToIncludeGeoCoordinate(
                    segment.geoCoordinates[1]
                );
            }

            return this.trackedRides.filter(
                (ride) =>
                    this.showOnMapService.getShouldShow(ride.id) &&
                    mapBounds.intersectsGeoBounds(ride.bounds)
            );
        }

        return [];
    }

    private getSmallestUnusedRouteNumber(routeNumbers: number[]): number {
        const filteredRouteNumbers = [...new Set(routeNumbers)];
        filteredRouteNumbers.sort((a, b) => a - b);

        let smallestUnused = 1;
        for (const number of filteredRouteNumbers) {
            if (number === smallestUnused) {
                smallestUnused++;
            } else {
                break;
            }
        }

        return smallestUnused;
    }

    private moveUnsavedLoggedOutRideToUserLocalStorage(
        unsavedPlannedRide: PlannedRide
    ): void {
        if (this.uuidService.isEmpty(unsavedPlannedRide.id)) {
            const unsavedLoggedOutRide = this.localStore.get(
                UNSAVED_PLANNED_RIDE_KEY
            );
            const unsavedLoggedOutRideExists = Boolean(unsavedLoggedOutRide);

            if (unsavedLoggedOutRideExists) {
                // move unsaved rides created while logged out to user prefix local storage
                this.localStore.remove(UNSAVED_PLANNED_RIDE_KEY);
                this.savePlannedRideLocally(unsavedPlannedRide);
            }
        }
    }

    private isValidId(id: string): boolean {
        const isWholeNumber = /^[0-9]+$/.test(id);
        return Boolean(id) && (isWholeNumber || this.uuidService.isUuid(id));
    }
}
